import React, { useCallback, useMemo } from 'react';
import { useStockData } from 'hooks/useStockData.ts';
import { useQueue } from 'contexts/QueueContext.tsx';
import './StockOverlay.css';

// Empty/placeholder components retained for styles reference if needed
const StockOverlayEmpty: React.FC = () => (
  <div className="stock-overlay">
    <div className="stock-overlay__empty">
      <div className="stock-overlay__empty-message">
        No items in stock
      </div>
      <div className="stock-overlay__empty-submessage">
        Scan barcodes to consume items
      </div>
    </div>
  </div>
);

interface StockOverlayProps {
  onHide?: () => void;
}

const StockOverlay: React.FC<StockOverlayProps> = ({ onHide }) => {
  const { consumeStockItemsMap, units, refreshAll } = useStockData();
  
  // Use the unified queue system only for consume operations
  const {
    addConsumeOperation,
  } = useQueue();

  const [_lastDecrement, setLastDecrement] = React.useState<{
    productId: number;
    position: { x: number; y: number };
    timestamp: number;
  } | null>(null);

  // Convert stockItemsMap to array and filter for "piece" items only
  const pieceStockItems = useMemo(() => {
    if (!units) return [] as Array<unknown> as any[];
    
    // Find the "piece" quantity unit ID
    const pieceUnitId = (units as Array<{ id: number; name: string }>).find(unit => 
      unit.name.toLowerCase() === 'piece' || 
      unit.name.toLowerCase() === 'pieces'
    )?.id;

    if (!pieceUnitId) return [] as Array<unknown> as any[];

    // Filter stock items map for only piece items and convert to array
    const items = Array.from(consumeStockItemsMap.values())
      .filter(item => item.product.qu_id_stock === pieceUnitId)
      .sort((a, b) => a.product.name.localeCompare(b.product.name));

    return items;
  }, [consumeStockItemsMap, units]);

  // Handle item click - consume stock
  const handleDecrement = useCallback((productId: number, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setLastDecrement({
      productId,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      },
      timestamp: Date.now(),
    });

    // Add consume operation to queue (with immediate optimistic update)
    addConsumeOperation(productId);
  }, [addConsumeOperation]);



  if (pieceStockItems.length === 0) {
    return <StockOverlayEmpty />;
  }

  return (
    <div className="stock-overlay">
      <div className="stock-overlay__header">
        <h3>In Stock (Piece Items)</h3>
        <button
          type="button"
          className="stock-overlay__hide-button"
          onClick={onHide}
        >
          Hide Stock
        </button>
      </div>
      <div
        className="stock-overlay__content"
        style={{ "--stock-columns": 2 } as React.CSSProperties}
      >
        {pieceStockItems.map((item) => (
          <div
            key={item.stock.product_id}
            className="stock-overlay__item"
            onClick={(e) => handleDecrement(item.stock.product_id, e)}
            style={{ cursor: 'pointer' }}
            title="Click to decrement by 1"
          >
            <div className="stock-overlay__item-name">
              {item.product.name}
            </div>
            <div className="stock-overlay__item-details">
              <span className="stock-overlay__item-amount">
                {item.stock.amount}
              </span>
              <span className="stock-overlay__item-location">
                {item.location.name}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StockOverlay; 